import 'swiper/css';
import 'swiper/css/pagination';

import { useId, useRef, useState } from 'react';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, type SwiperClass, SwiperSlide } from 'swiper/react';

import Icon, { IconName } from '@/components/Icon/Icon';
import { SelectedImage } from '@/components/ProductPageBase/SelectedImage/SelectedImage';
import useViewPortSize from '@/hooks/useViewportSize';

import useStyles from './Carousel.styles';

interface CarouselProps {
  images: string[];
  isOnDark?: boolean;
  selectedImageOverride?: string | undefined;
}

export const Carousel: React.FC<CarouselProps> = ({
  images,
  isOnDark = false,
  selectedImageOverride,
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [shouldHideNavigation, setShouldHideNavigation] =
    useState<boolean>(false);
  const { classes, cx } = useStyles({ isOnDark });
  const swiperRef = useRef<SwiperClass>(null);
  const prevElId = useId();
  const nextElId = useId();
  const paginationId = useId();

  const { isExtraSmall } = useViewPortSize();

  return (
    <div className={classes.container}>
      {(!isExtraSmall || images.length === 1) && (
        <SelectedImage
          image={selectedImageOverride ?? images[selectedImageIndex]}
          zoomImageOnHover={true}
        />
      )}
      {images.length > 1 && (
        <>
          <div className={classes.carousel}>
            <button
              id={prevElId}
              type='button'
              aria-label='Previous image'
              className={cx(
                classes.navigationButton,
                shouldHideNavigation && classes.hidden
              )}
            >
              <Icon name={IconName.ARROW_LEFT} width={24} />
            </button>
            <div className={classes.swiperContainer}>
              <Swiper
                navigation={{
                  prevEl: `#${prevElId}`,
                  nextEl: `#${nextElId}`,
                  disabledClass: classes.navigationButtonDisabled,
                }}
                modules={[Navigation, Pagination]}
                slidesPerView={'auto'}
                onSwiper={(swiper: SwiperClass) => {
                  swiperRef.current = swiper;

                  if (swiper.isBeginning && swiper.isEnd) {
                    setShouldHideNavigation(true);
                  }
                }}
                pagination={{
                  el: `#${paginationId}`,
                  clickable: true,
                  bulletActiveClass: 'bullet-active',
                  bulletClass: 'bullet',
                  horizontalClass: classes.horizontal,
                  // biome-ignore lint/correctness/noUnusedFunctionParameters: Automated migration to Biome
                  renderBullet: (index: number, className: string) => {
                    return `<svg class="${className}" width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="4" cy="4" r="4" fill="currentColor"/>
                </svg>`;
                  },
                }}
              >
                {images.map((image, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                  <SwiperSlide className={classes.swiperSlide} key={index}>
                    {/** biome-ignore lint/a11y/noStaticElementInteractions: Automated migration to Biome */}
                    {/** biome-ignore lint/a11y/useKeyWithClickEvents: Automated migration to Biome */}
                    <div
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelectedImageIndex(index)}
                    >
                      {/** biome-ignore lint/performance/noImgElement: Automated migration to Biome */}
                      {/** biome-ignore lint/a11y/useAltText: Automated migration to Biome */}
                      <img src={image} className={classes.carouselImage} />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
            <button
              id={nextElId}
              type='button'
              aria-label='Next image'
              className={cx(
                classes.navigationButton,
                shouldHideNavigation && classes.hidden
              )}
            >
              <Icon name={IconName.ARROW_RIGHT} width={24} />
            </button>
          </div>
          <div className={classes.paginationContainer}>
            <div id={paginationId} className={classes.pagination}></div>
          </div>
        </>
      )}
    </div>
  );
};
